import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import type { AppError } from '../../api/contract';
import { useRepository } from '../../data/RepositoryProvider';
import { MutationError, toDialogError } from '../partners/PartnerFormDialog';
import { gamesRepositoryToken } from './repository';
import type { GameDetail, GameTypeDetail, MasterStatus } from './types';

type Target =
  | { readonly kind: 'gameType'; readonly item: GameTypeDetail }
  | { readonly kind: 'game'; readonly item: GameDetail };

export function GameStatusDialog({
  onClose,
  onSaved,
  open,
  target,
}: {
  readonly onClose: () => void;
  readonly onSaved: () => void;
  readonly open: boolean;
  readonly target: Target;
}) {
  const repository = useRepository(gamesRepositoryToken);
  const { t } = useTranslation();
  const guard = useRef(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<Pick<AppError, 'message' | 'requestId'> | null>(null);
  const {
    formState: { errors },
    handleSubmit,
    register,
    reset,
    watch,
  } = useForm<{ readonly status: MasterStatus; readonly reason: string }>({
    defaultValues: { status: target.item.status, reason: '' },
  });
  const proposed = watch('status');

  useEffect(() => {
    if (!open) return;
    reset({ status: target.item.status, reason: '' });
    setError(null);
    setBusy(false);
    guard.current = false;
  }, [open, reset, target.item.status]);

  const submit = handleSubmit(async (values) => {
    if (guard.current) return;
    guard.current = true;
    setBusy(true);
    setError(null);
    try {
      if (target.kind === 'gameType') {
        await repository.changeGameTypeStatus(target.item.id, {
          status: values.status,
          reason: values.reason.trim(),
        });
      } else {
        await repository.changeGameStatus(target.item.id, {
          status: values.status,
          reason: values.reason.trim(),
        });
      }
      onSaved();
      onClose();
    } catch (submitError) {
      setError(toDialogError(submitError, t('games.errors.lifecycle')));
    } finally {
      guard.current = false;
      setBusy(false);
    }
  });

  return (
    <Dialog
      aria-describedby="game-status-impact"
      fullWidth
      onClose={() => !guard.current && onClose()}
      open={open}
    >
      <DialogTitle>{t('games.status.title')}</DialogTitle>
      <DialogContent>
        <Stack
          component="form"
          id="game-status-form"
          noValidate
          onSubmit={submit}
          spacing={2}
          sx={{ pt: 1 }}
        >
          {error === null ? null : <MutationError error={error} />}
          <Alert severity={target.item.dependencyBlockers.length > 0 ? 'error' : 'warning'}>
            <Typography id="game-status-impact">
              {t('games.status.impact', {
                code: target.item.code,
                current: target.item.status,
                next: proposed,
              })}
            </Typography>
            <Typography variant="body2">{t('games.status.dependencies')}</Typography>
            {target.item.dependencyBlockers.length === 0 ? null : (
              <Typography component="p" sx={{ overflowWrap: 'anywhere' }} variant="body2">
                {t('games.status.blockers', {
                  blockers: target.item.dependencyBlockers.join(', '),
                })}
              </Typography>
            )}
            <Typography variant="body2">{t('games.status.closedTerminal')}</Typography>
          </Alert>
          <TextField
            defaultValue={target.item.status}
            label={t('games.fields.proposedStatus')}
            select
            {...register('status')}
          >
            <MenuItem value="ACTIVE">ACTIVE</MenuItem>
            <MenuItem value="SUSPENDED">SUSPENDED</MenuItem>
            <MenuItem value="CLOSED">CLOSED</MenuItem>
          </TextField>
          <TextField
            error={errors.reason !== undefined}
            helperText={errors.reason?.message}
            label={t('games.fields.reason')}
            minRows={2}
            multiline
            required
            {...register('reason', {
              maxLength: { value: 2000, message: t('games.validation.reasonMaximum') },
              validate: (value) => value.trim().length > 0 || t('games.validation.reasonRequired'),
            })}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button disabled={busy} onClick={onClose}>
          {t('actions.cancel')}
        </Button>
        <Button
          color="warning"
          form="game-status-form"
          loading={busy}
          type="submit"
          variant="contained"
        >
          {t('actions.confirm')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
